/**
 * Folha "Estacionei aqui": onde (minha posição, ou um lugar pela busca),
 * uma nota curta ("Goofy 14") e, se quiser, a foto da placa da fileira —
 * reduzida no aparelho e enviada direto ao armazenamento. A foto não vai
 * para as Memórias.
 */
import { uuidv7 } from '@nv/core';
import { useRef, useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { useCompartilharPosicao } from '../mapa/compartilhar';
import { enviarVariante } from '../memorias/api';
import { fotoReduzida } from '../memorias/imagem';
import { LocalField, type LugarDaAtividade } from '../roteiro/LocalField';
import { carroApi, useInvalidarCarro } from './api';
import { Sheet } from '../../ui/Sheet';

export function EstacioneiAqui({ onFechar, onGuardado }: { onFechar: () => void; onGuardado?: () => void }) {
  const { tripId } = useTrip();
  const minha = useCompartilharPosicao((s) => s.ultima);
  const avisar = useInvalidarCarro(tripId);
  const [nota, setNota] = useState('');
  const [nomeDoLugar, setNomeDoLugar] = useState('');
  const [lugar, setLugar] = useState<LugarDaAtividade | null>(null);
  const [usarMinha, setUsarMinha] = useState(!!minha);
  const [foto, setFoto] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);

  const coords = usarMinha && minha ? { latitude: minha.latitude, longitude: minha.longitude } : lugar ? { latitude: lugar.latitude, longitude: lugar.longitude } : null;
  const pode = !!coords;

  async function marcar() {
    if (!coords) return;
    setEnviando(true);
    setErro(null);
    try {
      const reduzida = foto ? await fotoReduzida(foto) : null;
      const r = await carroApi.estacionar(tripId, {
        id: uuidv7(),
        latitude: coords.latitude,
        longitude: coords.longitude,
        note: nota.trim() || (lugar ? nomeDoLugar.trim() : '') || null,
        photo: reduzida ? { mimeType: 'image/jpeg', bytes: reduzida.size } : null,
      });
      if (reduzida && r.upload) {
        await enviarVariante(r.upload, reduzida);
        await carroApi.fotoPronta(tripId, r.car.id);
      }
      avisar();
      onGuardado?.();
      onFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível marcar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet titleId="carro-marcar" onFechar={onFechar}>
        <h2 className="sheet__title" id="carro-marcar">Estacionei aqui</h2>
        <div className="chip-row" role="radiogroup" aria-label="Lugar">
          <button type="button" role="radio" aria-checked={usarMinha} className={usarMinha ? 'pick pick--on' : 'pick'} disabled={!minha} onClick={() => setUsarMinha(true)}>
            Minha posição{!minha ? ' (ligue o mapa)' : ''}
          </button>
          <button type="button" role="radio" aria-checked={!usarMinha} className={!usarMinha ? 'pick pick--on' : 'pick'} onClick={() => setUsarMinha(false)}>
            Escolher um lugar
          </button>
        </div>
        {!usarMinha && <LocalField rotulo="Estacionamento" nome={nomeDoLugar} lugar={lugar} onChange={(nome, l) => { setNomeDoLugar(nome); setLugar(l); }} />}
        <label className="field">
          <span className="field__label">Fileira / setor (opcional)</span>
          <input className="field__input" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Goofy 14, piso 2, vaga 118…" maxLength={80} aria-label="Fileira ou setor" />
        </label>
        <input ref={arquivo} type="file" accept="image/*" capture="environment" hidden onChange={(e) => setFoto(e.target.files?.[0] ?? null)} data-testid="carro-foto" />
        <button type="button" className={foto ? 'carro__foto-btn carro__foto-btn--com' : 'carro__foto-btn'} onClick={() => arquivo.current?.click()}>
          <Icon name="memorias" size={18} /> {foto ? `Foto: ${foto.name}` : 'Foto da placa da fileira'}
        </button>
        <p className="sheet__text">A foto fica só aqui, para a sua família achar o carro — não entra nas Memórias e some quando você pegar o carro.</p>
        {erro && <p className="auth-error" role="alert">{erro}</p>}
        <div className="fin__ctas">
          <button type="button" className="nv-btn nv-btn--secondary" onClick={onFechar}>Cancelar</button>
          <button type="button" className="nv-btn nv-btn--primary" disabled={!pode || enviando} onClick={() => void marcar()}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
    </Sheet>
  );
}
